import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { DataService } from '../services/DataService';
import { FamilyTree } from '../models/types';

// Define the context type
interface FamilyTreeContextType {
  familyTree: FamilyTree;
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
}

// Create context with default values
const FamilyTreeContext = createContext<FamilyTreeContextType>({
  familyTree: {
    id: '',
    name: '',
    individuals: [],
    familyUnits: [],
    childRelationships: [],
    emotionalRelationships: [],
    createdAt: '',
    updatedAt: ''
  },
  loading: false,
  error: null,
  refreshData: async () => {}
});

// Custom hook for using the context
export const useFamilyTree = () => useContext(FamilyTreeContext);

// Provider component
export const FamilyTreeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [familyTree, setFamilyTree] = useState<FamilyTree>({
    id: '',
    name: '',
    individuals: [],
    familyUnits: [],
    childRelationships: [],
    emotionalRelationships: [],
    createdAt: '',
    updatedAt: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load family tree data
  const loadFamilyTree = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await DataService.getFamilyTree();
      setFamilyTree(data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading family tree data:', err);
      setError('Error loading family tree data');
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadFamilyTree();
  }, []);

  // Refresh data function
  const refreshData = async () => {
    await loadFamilyTree();
  };

  // Context value
  const value = {
    familyTree,
    loading,
    error,
    refreshData
  };

  return <FamilyTreeContext.Provider value={value}>{children}</FamilyTreeContext.Provider>;
};
